// Server-side error reporting to the same Grafana Faro collector the browser
// uses (see src/instrumentation-client.ts).
//
// `@grafana/faro-web-sdk` only runs in browsers, but the collector (Alloy's
// faro.receiver) just accepts a JSON payload over HTTP from any client. Posting
// one from the Next.js server puts server-side failures (a page that throws
// while rendering, a route handler that crashes) into Loki next to the browser
// ones, with the same `kind=exception` shape the daily digest already reads.
//
// Fetch-only on purpose: this module is imported by `logger`, which the
// proxy/edge bundle also pulls in. Reporting must never throw out of the caller.
import { scrubPii } from '@/lib/scrub-pii';

const REPORT_TIMEOUT_MS = 2000;
const MAX_FRAMES = 30;

type Frame = {
  filename: string;
  function?: string;
  lineno?: number;
  colno?: number;
};

// "    at fn (file:///app/x.js:10:5)" or "    at file:///app/x.js:10:5"
const STACK_LINE = /^\s*at (?:(.+?) \()?(.+?):(\d+):(\d+)\)?$/;

export const parseStackFrames = (stack: string | undefined): Frame[] => {
  const frames: Frame[] = [];
  for (const line of (stack ?? '').split('\n')) {
    const match = STACK_LINE.exec(line);
    if (!match) continue;
    frames.push({
      function: match[1],
      filename: scrubPii(match[2]),
      lineno: Number(match[3]),
      colno: Number(match[4])
    });
    if (frames.length >= MAX_FRAMES) break;
  }
  // Faro lists the innermost frame last.
  return frames.reverse();
};

export type ServerErrorContext = Record<string, string | undefined>;

export const buildServerErrorPayload = (
  error: unknown,
  context: ServerErrorContext,
  app: { name: string; environment: string; version?: string }
) => {
  const err = error instanceof Error ? error : new Error(String(error));
  return {
    meta: {
      sdk: { name: 'faro-server-fetch', version: '1', integrations: [] },
      app: { ...app }
    },
    exceptions: [
      {
        type: err.name || 'Error',
        value: scrubPii(err.message),
        timestamp: new Date().toISOString(),
        stacktrace: { frames: parseStackFrames(err.stack) },
        context: Object.fromEntries(
          Object.entries(context)
            .filter(([, value]) => value !== undefined)
            .map(([key, value]) => [key, scrubPii(String(value))])
        )
      }
    ]
  };
};

/**
 * Fire-and-forget. Silent when Faro is not configured (local dev, PR builds),
 * and never throws: a failed report must not turn one error into two.
 */
export const reportServerError = (
  error: unknown,
  context: ServerErrorContext = {}
): void => {
  const faroUrl = process.env.NEXT_PUBLIC_FARO_URL;
  const environment = process.env.NEXT_PUBLIC_APP_ENV;
  if (!faroUrl || !environment) return;

  try {
    const payload = buildServerErrorPayload(
      error,
      { source: 'next-server', ...context },
      {
        name: 'ihrhelden-web',
        environment,
        version: process.env.NEXT_PUBLIC_APP_VERSION
      }
    );
    void fetch(faroUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(REPORT_TIMEOUT_MS)
    }).catch(() => {
      // The collector being down is not worth a second error.
    });
  } catch {
    // Same: reporting is best effort.
  }
};
